import React from 'react';
import Reveal from './Reveal';
import { SectionMarker } from './Gap';
import { Kern } from './Kern';
import { founders } from '../data';

const Person = ({ p, reverse }) => (
  <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start py-16 md:py-24 border-t border-[var(--border)]">
    <Reveal className={reverse ? 'md:order-2' : ''}>
      <div className="relative">
        <div className="aspect-[4/5] md:aspect-auto md:h-[680px] rounded-xl overflow-hidden bg-[var(--surface)] border border-[var(--border)]">
          <img src={p.img} alt={p.name} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" loading="lazy" />
        </div>
      </div>
    </Reveal>
    <Reveal delay={120} className={reverse ? 'md:order-1' : ''}>
      <div className="md:h-[680px] flex flex-col">
        <p className="label !text-[var(--accent)] mb-4">{p.role}</p>
        <h3 className="font-display text-[clamp(2rem,3.6vw,3.2rem)] font-medium mb-8 tracking-[-0.01em]">{p.name}</h3>
        <div className="flex flex-col gap-5 flex-1">
          {p.paras.map((para, i) => (
            <p key={i} className="text-[var(--muted)] leading-relaxed font-light w-full">{para}</p>
          ))}
        </div>
        <div className="mt-auto pt-10">
          <blockquote className="font-serif-c italic text-[1.5rem] md:text-[1.9rem] leading-snug text-[var(--text)] border-l-2 border-[var(--accent)] pl-6">{p.quote}</blockquote>
        </div>
      </div>
    </Reveal>
  </div>
);

const Founders = () => {
  return (
    <section id="founders" className="relative section-pad border-t border-[var(--border)] bg-[var(--bg-2)] scroll-mt-24">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Reveal><SectionMarker index={founders.index} label={founders.label} className="label-gap" /></Reveal>
        <Reveal>
          <h2 className="font-display font-medium text-[clamp(1.9rem,4.4vw,3.8rem)] leading-[1.08] tracking-[-0.01em] mb-8">
            Two people looked at the same problem from <span className="font-serif-c italic text-[var(--accent)] font-normal">completely different sides.</span>
          </h2>
        </Reveal>

        {/* Origin story + what Empax is - stacked one below the other */}
        <div className="flex flex-col gap-12 md:gap-16 mb-4">
          <div className="w-full">
            <Reveal>
              <p className="text-[var(--muted)]  leading-relaxed font-light max-w-[68ch]">
                It started as a conversation over a good meal that refused to end. Two people meeting for the first time kept circling one question: why do so many spaces look right and still feel like nothing? Somewhere between courses the talk stopped being small. A quiet conviction took shape, that a space, its story and its technology should be imagined together and never bolted on at the end. But by the end the idea had a name. That name was <span className="text-[var(--text)]">Empax</span>.
              </p>
            </Reveal>
          </div>
          <div className="w-full">
            <Reveal delay={140}>
              <div className="relative rounded-2xl px-6 py-12 md:px-16 md:py-14 mb-12 md:mb-[78px] overflow-hidden border border-[rgba(255,255,255,0.12)]" style={{ backgroundColor: 'var(--brand-blue)' }}>
                {/* blueprint grid */}
                <div className="pointer-events-none absolute inset-0 opacity-[0.14]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
                <span className="pointer-events-none absolute top-5 left-5 w-6 h-6 border-l-2 border-t-2 border-[var(--accent)]/70" />
                <span className="pointer-events-none absolute bottom-5 right-5 w-6 h-6 border-r-2 border-b-2 border-[var(--accent)]/70" />
                <p className="relative label !text-[var(--accent)] mb-[34px] text-left md:text-center">Introducing Empax Studio</p>
                <p className="relative font-serif-c italic text-white text-[1.5rem] md:text-[2.2rem] text-left md:text-center [text-wrap:balance] w-full">
                  “Empax Studio is an experience design and engineering studio that <span className="text-[var(--accent)]">synthesises architecture, creativity, storytelling and technology,</span> guided by innovation and the human journey to transform narratives into experiences.”
                </p>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal>
          <p data-testid="founders-support-text" className="font-serif-c italic text-[var(--accent)] text-[1.6rem] md:text-[2.2rem] text-left md:text-center leading-[1.4] mb-12 md:mb-[78px]">
            We engineer the invisible <span className="text-[var(--muted-2)] px-1">|</span><br className="md:hidden" /> We engineer for human
          </p>
        </Reveal>


        {founders.people.map((p, i) => <Person key={p.name} p={p} reverse={i % 2 === 1} />)}

        {/* Inside the studio, the team beyond the two founders */}
        <div className="pt-[var(--section-pad)] border-t border-[var(--border)]">
          <div className="grid md:grid-cols-12 gap-8 md:gap-16 mb-14">
            <div className="md:col-span-5">
              <Reveal>
                <p className="label !text-[var(--accent)] label-gap">Inside The Studio</p>
                <h3 className="font-display font-medium text-[clamp(1.8rem,3.4vw,3rem)] leading-[1.08] tracking-[-0.01em]">
                  More than two minds,<br /><span className="text-[var(--accent)]">a studio of synthesists.</span>
                </h3>
              </Reveal>
            </div>
            <div className="md:col-span-7 flex items-end justify-end">
              <Reveal delay={120}>
                <p className="text-[var(--muted)]  leading-relaxed font-light max-w-[60ch] text-left md:text-right md:ml-auto">
                  Beyond Tanya and Vijay is a team of distinct minds working as one. Each brings a different craft, but no one works in isolation. They innovate across disciplines, connect perspectives and build cohesive solutions. That is how a Synthesist works.
                </p>
              </Reveal>
            </div>
          </div>

          <Reveal>
            <div className="relative rounded-2xl overflow-hidden border border-[var(--border)] aspect-[16/9]" style={{ backgroundColor: 'var(--bg-2)' }}>
              <img src="/studio-team-opt.jpg" alt="Inside the Empax studio" className="absolute inset-0 w-full h-full object-cover object-center" loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 h-[62%] pointer-events-none" style={{ background: 'linear-gradient(0deg, rgba(10,13,23,0.88) 0%, rgba(10,13,23,0.5) 45%, rgba(10,13,23,0) 100%)' }} />
              <div className="absolute bottom-0 left-0 right-0 px-5 sm:px-7 md:px-10 pb-6 md:pb-[6%]">
                <p className="font-display text-white text-[clamp(0.78rem,2.4vw,2.1rem)] leading-[1.15] font-medium text-center [text-wrap:balance]">
                  Architects | Experience and UX Designers&nbsp;| <span className="whitespace-nowrap">Visual and Creative Minds |</span> Concept Creators and Technical Specialists.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Founders;
